import { useState } from 'react';
import { motion } from 'framer-motion';

interface TripReport {
  id: number;
  country_code: string;
  city?: string;
  title: string;
  content: string;
  experience_rating: number;
  tags: string[];
  upvotes: number;
  created_at: string;
}

interface TripReportCardProps {
  report: TripReport;
  onUpvote: (id: number) => Promise<void>;
  onReport: (id: number, reason: string) => Promise<void>;
}

export default function TripReportCard({ report, onUpvote, onReport }: TripReportCardProps) {
  const [upvoted, setUpvoted] = useState(false);
  const [upvoteLoading, setUpvoteLoading] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const handleUpvote = async () => {
    if (upvoted || upvoteLoading) return;
    setUpvoteLoading(true);
    try {
      await onUpvote(report.id);
      setUpvoted(true);
    } catch (e) {
      console.error('Upvote failed:', e);
    } finally {
      setUpvoteLoading(false);
    }
  };

  const handleReport = async (reason: string) => {
    setReportLoading(true);
    try {
      await onReport(report.id, reason);
      setReportMenuOpen(false);
    } catch (e) {
      console.error('Report failed:', e);
    } finally {
      setReportLoading(false);
    }
  };

  const formattedDate = new Date(report.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: report.created_at.includes(new Date().getFullYear().toString()) ? undefined : 'numeric'
  });

  // Star rating component
  const ratingStars = '★'.repeat(report.experience_rating);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3 hover:bg-white/[0.05] transition-colors"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-bold text-sm leading-tight">{report.title}</h4>
          {report.city && (
            <p className="text-white/50 text-xs mt-1">📍 {report.city}</p>
          )}
        </div>
        <div className="flex-shrink-0 text-right text-xs text-white/40">
          {formattedDate}
        </div>
      </div>

      {/* Rating */}
      <div className="flex items-center gap-2">
        <span className="text-sm">{ratingStars}</span>
        <span className="text-xs text-white/50">{report.experience_rating}/5</span>
      </div>

      {/* Content preview */}
      <p className="text-white/70 text-sm line-clamp-3">{report.content}</p>

      {/* Tags */}
      {report.tags && report.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {report.tags.slice(0, 3).map((tag, i) => (
            <span
              key={i}
              className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-cannabis-500/10 text-cannabis-400 border border-cannabis-500/20"
            >
              {tag}
            </span>
          ))}
          {report.tags.length > 3 && (
            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-white/5 text-white/40">
              +{report.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2 border-t border-white/10">
        <button
          onClick={handleUpvote}
          disabled={upvoted || upvoteLoading}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            upvoted
              ? 'bg-cannabis-500/20 text-cannabis-400'
              : 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white disabled:opacity-50'
          }`}
        >
          <span className="text-xs">▲</span> {report.upvotes}
        </button>

        <div className="relative">
          <button
            onClick={() => setReportMenuOpen(!reportMenuOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          >
            🚩
          </button>

          {reportMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-40 bg-black/90 border border-white/20 rounded-lg overflow-hidden z-10 shadow-lg">
              {['Inappropriate', 'Spam', 'Fake', 'Other'].map(reason => (
                <button
                  key={reason}
                  onClick={() => handleReport(reason)}
                  disabled={reportLoading}
                  className="w-full text-left px-3 py-2 text-xs text-white/70 hover:bg-red-500/20 hover:text-red-400 disabled:opacity-50 transition-colors"
                >
                  {reason}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
